import React from 'react'
import {useIntl} from 'react-intl'
import {HStack, StackProps, TextProps, useMultiStyleConfig} from '@chakra-ui/react'
import {Chips} from '_uiComponents'
import {RefinementsType} from '_types'

type SelectedFiltersType = {apiLabel: string; value: string; uiLabel: string}

interface SelectedRefinementsProps {
    filters?: RefinementsType
    selectedFilterValues: {[key: string]: string}
    isInsideDrawer?: boolean
    toggleFilter: (filter: {value: string}, attributeId: string, selected: boolean, allowMultiple: boolean, action?: string) => void
}

/**
 * SelectedRefinements component displays a list of selected filters and provides
 * functionality to remove individual filters or reset all filters.
 *
 * @component
 * @param {SelectedRefinementsProps} props - The properties passed to the component.
 * @param {Record<string, string>} props.filters - The available filters, including their attributes and values.
 * @param {Record<string, string>} props.selectedFilterValues - The currently selected filter values, keyed by filter attribute.
 * @param {boolean} [props.isInsideDrawer=false] - Determines if the component is rendered inside a drawer, applying specific styles.
 * @param {Function} props.toggleFilter - Callback function to toggle a filter's selection state.
 *
 * @returns {JSX.Element | null} The rendered SelectedRefinements component or null if no filters are selected.
 *
 * @example
 * ```tsx
 * <SelectedRefinements
 *   filters={filters}
 *   selectedFilterValues={selectedFilterValues}
 *   isInsideDrawer={true}
 *   toggleFilter={toggleFilterHandler}
 * />
 * ```
 *
 */
const SelectedRefinements = ({filters, selectedFilterValues, isInsideDrawer = false, toggleFilter}: SelectedRefinementsProps) => {
    const styles = useMultiStyleConfig('SelectedRefinements') as Record<string, StackProps & TextProps>
    const {formatMessage} = useIntl()

    const priceFilterValues = filters?.find((filter) => filter.attributeId === 'price')

    const selectedFilters: SelectedFiltersType[] = []

    for (const key in selectedFilterValues) {
        const currFilterValues = selectedFilterValues[key].split('|')
        currFilterValues?.forEach((filter) => {
            const selected = {
                uiLabel: key === 'price' ? priceFilterValues?.values?.find((priceFilter) => priceFilter.value === filter)?.label || '' : filter,
                value: key,
                apiLabel: filter.replace(/&/g, '%26'),
            }

            if (selected.value === 'c_filter-size') {
                selected.uiLabel =
                    filters?.find((f) => f.attributeId === 'c_filter-size')?.values?.find((v) => v.label === filter)?.description ||
                    selected.uiLabel.split('-')[0]
            }

            if (selected.value !== 'htype' && selected.value !== 'cgid' && selected.value !== 'price' && selected.value !== 'pmid') {
                selectedFilters.push(selected)
            }
        })
    }

    return selectedFilters?.length > 0 ? (
        <HStack {...styles.wrapper} {...(isInsideDrawer ? styles.drawerVariant : {})} data-testid="selected-refinements">
            {selectedFilters?.map((filter, idx) => {
                const resetFiltersLabel = formatMessage({id: 'assistive.text.remove.filter.button', defaultMessage: 'Remove filter {0}'}, {0: filter.uiLabel})
                return (
                    <Chips key={idx} ariaLabelText={resetFiltersLabel} onClick={() => toggleFilter({value: filter.apiLabel}, filter.value, true, true)}>
                        {filter.uiLabel}
                    </Chips>
                )
            })}
        </HStack>
    ) : (
        <></>
    )
}

export {SelectedRefinements}
